import React, { useContext } from 'react';
import { useStyletron } from 'baseui';
import { ARTWORK_TYPE, Banner, KIND } from 'baseui/banner';
import { FormControl } from 'baseui/form-control';
import { Heading, HeadingLevel } from 'baseui/heading';
import { Segment, SegmentedControl } from 'baseui/segmented-control';
import { Select } from 'baseui/select';
import { faMapMarkerAlt, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SimpleEnquiries } from '@gtintel/platform-types';
import { jurisdictionComparator } from '.';
import Editor from '../../components/Editor';
import { AddWithName, List, ListContext } from '../../components/List';
import { Title } from '../../components/Title';
import { ProjectContext, SubjectsContext } from '../context';
const Subject = ({
  isAssigned,
  subject
}) => {
  const [css, theme] = useStyletron();
  const {
    name,
    id,
    description,
    subject_type: subjectType
  } = subject;
  const {
    edit,
    jurisdictions,
    jurisdictionOptions,
    subjectTypes
  } = useContext(SubjectsContext);
  const projectCtx = useContext(ProjectContext);
  const title = <Heading>
      <Title text={name} onChange={newValue => edit(id, {
      name: newValue
    })} />
    </Heading>;
  const jurisdictionSelect = jurisdictionOptions().map(({
    name,
    id
  }) => ({
    label: name,
    id
  }));
  const jurisdictionValue = jurisdictions(id).sort(jurisdictionComparator).map(({
    name,
    id
  }) => ({
    label: name,
    id
  }));
  const foundSubjectTypes = subjectTypes();
  const items = [];
  for (const prop in foundSubjectTypes) {
    items.push(<Segment key={prop} label={foundSubjectTypes[prop]} />);
  }
  return <div className={css({
    background: theme.colors.backgroundPrimary,
    border: 'solid 1px #e2e2e2',
    borderRadius: '10px',
    padding: '1em 1.5em',
    margin: '1.0em'
  })}>
      <FormControl>{title}</FormControl>
      {isAssigned && <Banner kind={KIND.warning} artwork={{
      icon: () => <FontAwesomeIcon icon={faWarning} size="lg" />,
      type: ARTWORK_TYPE.badge
    }}>
          Changes are restricted because the subject is included in an
          assignment.
        </Banner>}
      <FormControl label={() => 'Instructions'}>
        <div className="editor-container">
          <Editor onChange={description => edit(id, {
          description: description
        })} value={description || ''} />
        </div>
      </FormControl>
      <FormControl label={() => 'Jurisdictions'}>
        <Select options={jurisdictionSelect} value={jurisdictionValue} multi placeholder="Select..." onChange={params => {
        const jurisdictionSet = new Set();
        params.value.forEach(({
          id
        }) => jurisdictionSet.add(id.toString()));
        jurisdictions(subject.id).filter(({
          id,
          name
        }) => {
          return projectCtx.isAssignedSubjectJurisdiction(subject.id, id);
        }).forEach(({
          id
        }) => jurisdictionSet.add(id.toString()));
        edit(id, {
          jurisdictions: Array.from(jurisdictionSet)
        });
      }} closeOnSelect={false} />
      </FormControl>
      <FormControl label={() => 'Type'}>
        <SegmentedControl disabled={isAssigned} activeKey={subjectType} onChange={({
        activeKey
      }) => {
        edit(id, {
          subject_type: activeKey.toLocaleString()
        });
      }}>
          {items}
        </SegmentedControl>
      </FormControl>
      {(subject.address || subject.category || !!subject.number_of_enquiries) && <h6>Additional Info</h6>}
      {subject.address && <div className={css({
      display: 'grid',
      gridColumnGap: '8px',
      gridTemplateColumns: '[icon] 20px [text] auto'
    })}>
          <FontAwesomeIcon icon={faMapMarkerAlt} style={{
        color: 'black'
      }} size="xl" />
          <p className={css({
        alignSelf: 'center',
        gridColumnStart: 'text',
        marginBottom: '0'
      })}>
            {subject.address.split('\n').join(', ')}
          </p>
        </div>}
      <div>
        <div className={css({
        marginTop: '14px',
        p: {
          marginBottom: '0'
        }
      })}>
          {subject.category && <p>
              <b>Category: </b>
              {subject.category}
            </p>}
          {!!subject.number_of_enquiries && <p>
              <b>Number of enquiries: </b>
              {SimpleEnquiries[subject.number_of_enquiries]}
            </p>}
        </div>
      </div>
    </div>;
};
export const Subjects = () => {
  const subjectsCtx = useContext(SubjectsContext);
  const projectCtx = useContext(ProjectContext);
  const anyAssigned = subjectsCtx.list().filter(s => projectCtx?.isAssignedSubject(s.id)).length > 0;
  return <div>
      <HeadingLevel>
        <ListContext.Provider value={subjectsCtx}>
          <List inline reorderable={!anyAssigned} items={subjectsCtx.list().map(s => ({
          ...s,
          removeable: !projectCtx.isAssignedSubject(s.id)
        }))} renderItem={subject => <Subject isAssigned={projectCtx.isAssignedSubject(subject.id)} subject={subject} />} />
          <AddWithName />
        </ListContext.Provider>
      </HeadingLevel>
    </div>;
};